import { useState } from "react";
import {
  useGetUserAiUsage,
  useGetUserAiUsageDaily,
  formatFeatureName,
} from "@workspace/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Cpu, DollarSign, Star, Zap, ChevronRight } from "lucide-react";

const includeCredentials: RequestInit = { credentials: "include" };

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatMonth(ym: string): string {
  const [year, month] = ym.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString("default", { month: "long", year: "numeric" });
}

function formatPlanType(planType: string): string {
  switch (planType) {
    case "free":
      return "Free";
    case "pro":
      return "Pro";
    case "enterprise":
      return "Enterprise";
    default:
      return planType.charAt(0).toUpperCase() + planType.slice(1);
  }
}

function formatDayLabel(day: string): string {
  // day is YYYY-MM-DD (UTC bucket); show just the day-of-month number.
  return String(Number(day.slice(8, 10)));
}

function FeatureDailyTrendDialog({
  feature,
  onClose,
}: {
  feature: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, refetch } = useGetUserAiUsageDaily(
    { feature },
    { request: includeCredentials },
  );

  const days = data?.days ?? [];
  const maxTokens = Math.max(1, ...days.map((d) => d.tokensUsed));
  const totalTokens = days.reduce((sum, d) => sum + d.tokensUsed, 0);
  const totalCost = days.reduce((sum, d) => sum + d.estimatedCostUsd, 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dialog-ai-usage-daily">
        <DialogHeader>
          <DialogTitle>{formatFeatureName(feature)}</DialogTitle>
          <DialogDescription>
            Daily usage{data ? ` · ${formatMonth(data.billingMonth)}` : ""}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="py-8 text-center space-y-3">
            <p className="text-sm text-destructive">Failed to load daily usage.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-ai-usage-daily">
              Retry
            </Button>
          </div>
        )}

        {data && days.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No usage recorded for this feature this month.
          </p>
        )}

        {data && days.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-medium">Total This Month</p>
                <p className="text-xs text-muted-foreground">${totalCost.toFixed(4)}</p>
              </div>
              <p className="text-lg font-bold">{formatTokens(totalTokens)}</p>
            </div>

            <div>
              <p className="text-xs font-medium tracking-wider text-muted-foreground mb-2">
                TOKENS BY DAY
              </p>
              <div
                className="flex items-end gap-1 h-40 rounded-lg border p-4"
                data-testid={`chart-ai-usage-daily-${feature}`}
              >
                {days.map((d) => (
                  <div key={d.day} className="flex-1 flex flex-col items-center h-full min-w-0">
                    <span className="text-[9px] text-muted-foreground truncate max-w-full">
                      {formatTokens(d.tokensUsed)}
                    </span>
                    <div className="flex-1 w-full flex flex-col justify-end px-0.5">
                      <div
                        className="w-full rounded-sm bg-primary"
                        style={{
                          height: `${Math.max(4, Math.round((d.tokensUsed / maxTokens) * 100))}%`,
                          minHeight: 3,
                        }}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-1">
                      {formatDayLabel(d.day)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium tracking-wider text-muted-foreground mb-2">
                DAILY DETAIL
              </p>
              <div className="rounded-lg border divide-y max-h-60 overflow-y-auto">
                {days.map((d) => (
                  <div
                    key={d.day}
                    className="flex items-center justify-between px-4 py-2.5"
                    data-testid={`row-ai-usage-day-${d.day}`}
                  >
                    <div>
                      <p className="text-sm font-medium">{d.day}</p>
                      <p className="text-xs text-muted-foreground">
                        ${d.estimatedCostUsd.toFixed(4)}
                      </p>
                    </div>
                    <p className="text-sm font-bold">{formatTokens(d.tokensUsed)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AiUsageTab() {
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useGetUserAiUsage(undefined, {
    request: includeCredentials,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-3">
          <p className="text-sm text-destructive">Failed to load AI usage.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-ai-usage">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const breakdown = data.breakdown ?? [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>AI Usage</CardTitle>
              <CardDescription className="mt-1">
                {formatMonth(data.billingMonth)}
              </CardDescription>
            </div>
            <Badge variant="secondary">{formatPlanType(data.planType)} plan</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 rounded-lg border p-4">
              <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Cpu className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Tokens Used</p>
                <p className="font-semibold" data-testid="text-ai-usage-tokens">
                  {formatTokens(data.tokensUsed)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-4">
              <div className="w-9 h-9 rounded-md bg-green-500/10 flex items-center justify-center shrink-0">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Estimated Cost</p>
                <p className="font-semibold" data-testid="text-ai-usage-cost">
                  ${data.estimatedCostUsd.toFixed(4)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-4">
              <div className="w-9 h-9 rounded-md bg-amber-500/10 flex items-center justify-center shrink-0">
                <Star className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Plan</p>
                <p className="font-semibold">{formatPlanType(data.planType)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Usage by Feature</CardTitle>
            <CardDescription>
              Click a feature to see its day-by-day usage and spot cost spikes.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {breakdown.map((item) => (
                <button
                  type="button"
                  key={item.feature}
                  className="w-full flex items-center gap-3 px-6 py-3.5 text-left hover:bg-muted/50 transition-colors"
                  onClick={() => setSelectedFeature(item.feature)}
                  data-testid={`row-ai-usage-feature-${item.feature}`}
                  aria-label={`View daily trend for ${formatFeatureName(item.feature)}`}
                >
                  <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {formatFeatureName(item.feature)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ${item.estimatedCostUsd.toFixed(4)}
                    </p>
                  </div>
                  <span className="text-sm font-bold">{formatTokens(item.tokensUsed)}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        AI usage is metered and billed monthly. Costs reflect tokens consumed by the AI coach, goal
        suggestions, journal insights, and other AI-powered features.
      </p>

      {selectedFeature !== null && (
        <FeatureDailyTrendDialog
          feature={selectedFeature}
          onClose={() => setSelectedFeature(null)}
        />
      )}
    </div>
  );
}
